import moment from 'moment';
import { HotelSearchRequest, RoomSearchInfo } from './__generated';
import { formatDateOnly } from './config';
import { convert } from 'url-slug';
import { BoardType } from './constants';

export const AppUrls = {
  HomePage: '/',
  HotelSearch: '/hotel-search',
  HotelDetail: '/:slug/hotel/:hotelCode',
  BookingConfirmation: '/booking/confirmation/:sessionId',
  BookingPassenger: '/booking/passenger/:sessionId',
  BookingPayment: '/booking/payment/:sessionId',
  OnHoldPayment: '/booking/on-hold/payment/:bookingId',
  BookingSuccess: '/booking/success/:bookingId',
  BookingFailed: '/booking/failed/:bookingId',
  BookingCancelledFailed: '/booking/cancelled/failed/:bookingId',
  BookingCancelledSuccess: '/booking/cancelled/success/:bookingId',
  BookingNeedToCheck: '/booking/need-to-check/:bookingId',
  BookingDetail: '/booking/detail/:bookingId',
  Page401: '/page-401',
  TermsAndConditions: '/terms-and-conditions',
  CampaignDetail: '/campaign/:slug',
  // agent
  __agent: '/agent',
  AgentDashboard: '/agent/dashboard',
  AgentProfile: '/agent/profile',
  AgentBookmarks: '/agent/bookmarks',
  AgentUsers: '/agent/users',
  AgentOnHoldBookings: '/agent/on-hold-bookings',
  AgentTodoList: '/agent/processing-bookings',
  AgentBookings: '/agent/bookings',
  AgentOrderBookings: '/agent/order-bookings',
  AgentReport: '/agent/report',
  AgentOrderDetail: '/agent/order-bookings/:orderId',
  // auth
  __unauthorized: '/auth',
  Login: '/auth/login',
  Logout: '/auth/logout',
  Register: '/auth/register',
  ForgotPassword: '/auth/user/forgot-password',
  ResetPassword: '/auth/reset-password',
  PasswordExpired: '/auth/password-expired',
  SSOLogin: '/sso/auth/login',

  //
  __createBookingConfirmationUrl: (sessionId: string) => AppUrls.BookingConfirmation.replace(':sessionId', sessionId),
  __createBookingPassengerUrl: (sessionId: string) => AppUrls.BookingPassenger.replace(':sessionId', sessionId),
  __createBookingPaymentUrl: (sessionId: string) => AppUrls.BookingPayment.replace(':sessionId', sessionId),
  __createBookingSuccessUrl: (bookingId: string) => AppUrls.BookingSuccess.replace(':bookingId', bookingId),
  __createBookingFailedUrl: (bookingId: string) => AppUrls.BookingFailed.replace(':bookingId', bookingId),
  __createBookingDetailUrl: (bookingId: string) => AppUrls.BookingDetail.replace(':bookingId', bookingId),
  __createBookingNeedToCheckUrl: (bookingId: string) => AppUrls.BookingNeedToCheck.replace(':bookingId', bookingId),
  __createBookingCancelledSuccessUrl: (bookingId: string) => AppUrls.BookingCancelledSuccess.replace(':bookingId', bookingId),
  __createBookingCancelledFailedUrl: (bookingId: string) => AppUrls.BookingCancelledFailed.replace(':bookingId', bookingId),
  __createOnHoldPaymentUrl: (bookingId: string) => AppUrls.OnHoldPayment.replace(':bookingId', bookingId),
  __createAgentOrderDetailUrl: (orderId: string) => AppUrls.AgentOrderDetail.replace(':orderId', orderId),
  __createSamePaxAndNextBookingUrl: (bookingRefNumber: string, rooms: RoomSearchInfo[]) => {
    let url = `${AppUrls.HomePage}?bookingRefNumber=${bookingRefNumber}`;
    rooms.forEach(room => {
      let roomValue = [room.noOfAdult, room.noOfChild, room.childAges?.join('.')].join('-');
      url += `&rooms=${roomValue}`;
    });
    return url;
  },
};

export const UnauthorizedUrl = [
  AppUrls.Login,
  AppUrls.ForgotPassword,
  AppUrls.ResetPassword,
  AppUrls.Register,
  AppUrls.Login,
  AppUrls.SSOLogin,
  AppUrls.TermsAndConditions,
  AppUrls.Logout,
];

export const PASSING_PAGE_PARAMS = ['bookingRefNumber', 'campaignRatePlan'];

interface CustomHotelSearchRequest extends HotelSearchRequest {
  provider?: string | null | undefined;
}

export const getCurrentSearchQuery = (name: string) => new URLSearchParams(window.location.search)?.get(name) || undefined;

export const generateHotelDetailUrl = (hotelName: string | null | undefined, hotelCode: string | null | undefined) => {
  let searchParams = getHotelSearchRequest(window.location.search);
  if (!hotelCode && !hotelName) {
    return AppUrls.HomePage;
  }
  if (!searchParams) {
    searchParams = {
      boardType: BoardType['No meal'] as any,
      checkinDate: moment()
        .add(1, 'M')
        .toDate(),
      checkoutDate: moment()
        .add(1, 'M')
        .add(2, 'day')
        .toDate(),
      locationCode: '',
      locationType: 1,
      rooms: [{ noOfAdult: 2, noOfChild: 0, roomIndex: 0 }],
    };
  }
  let url = `/${convert(hotelName)}/hotel/${hotelCode}`;
  url += `?checkinDate=${formatDateOnly(searchParams.checkinDate)}`;
  url += `&checkoutDate=${formatDateOnly(searchParams.checkoutDate)}`;
  searchParams.rooms.forEach(room => {
    let roomValue = [room.noOfAdult, room.noOfChild, room.childAges?.join('.')].join('-');
    url += `&rooms=${roomValue}`;
  });
  if (searchParams.sessionId) {
    url += `&sessionId=${searchParams.sessionId}`;
  }
  if (searchParams.boardType) {
    url += `&boardType=${searchParams.boardType}`;
  }
  if (searchParams.provider) {
    url += `&provider=${searchParams.provider}`;
  }
  let urlParams = new URLSearchParams(window.location.search);
  PASSING_PAGE_PARAMS.forEach(p => {
    if (urlParams.get(p)) url += `&${p}=${urlParams.get(p)}`;
  });
  return url;
};
export const generateHotelDetailUrlWithFullInfo = (
  hotelName: string | null | undefined,
  hotelCode: string | null | undefined,
  checkinDate: any,
  checkoutDate: any,
  boardType: any,
  rooms: RoomSearchInfo[],
  sessionId?: string,
  provider?: string | undefined,
) => {
  if (!hotelCode && !hotelName) {
    return AppUrls.HomePage;
  }
  let url = `/${convert(hotelName)}/hotel/${hotelCode}`;
  url += `?checkinDate=${formatDateOnly(checkinDate)}`;
  url += `&checkoutDate=${formatDateOnly(checkoutDate)}`;
  rooms.forEach(room => {
    let roomValue = [room.noOfAdult, room.noOfChild, room.childAges?.join('.')].join('-');
    url += `&rooms=${roomValue}`;
  });
  if (sessionId) {
    url += `&sessionId=${sessionId}`;
  }
  if (boardType) {
    url += `&boardType=${boardType}`;
  }
  if (provider) {
    url += `&provider=${provider}`;
  }
  let urlParams = new URLSearchParams(window.location.search);
  PASSING_PAGE_PARAMS.forEach(p => {
    if (urlParams.get(p)) url += `&${p}=${urlParams.get(p)}`;
  });
  return url;
};

export const generateHotelSearchUrl = (request: CustomHotelSearchRequest, sessionId?: string) => {
  let url: string = `/hotel-search`;
  url += `?locationCode=${request.locationCode}`;
  url += `&locationType=${request.locationType}`;
  url += `&checkinDate=${formatDateOnly(request.checkinDate)}`;
  url += `&checkoutDate=${formatDateOnly(request.checkoutDate)}`;
  url += `&boardType=${request.boardType}`;
  if (request.provider) {
    url += `&provider=${request.provider}`;
  }
  request.rooms.forEach(room => {
    let roomValue = [room.noOfAdult, room.noOfChild, room.childAges?.join('.')].join('-');
    url += `&rooms=${roomValue}`;
  });
  if (sessionId) {
    url += `&sessionId=${sessionId}`;
  }
  let urlParams = new URLSearchParams(window.location.search);
  PASSING_PAGE_PARAMS.forEach(p => {
    if (urlParams.get(p)) url += `&${p}=${urlParams.get(p)}`;
  });
  return url;
};
export const generateHotelSearchUrlWithLocation = (locationCode: string, locationType: any) => {
  let url: string = `/hotel-search`;
  const query = new URLSearchParams(window.location.search);

  url += `?locationCode=${locationCode}`;
  url += `&locationType=${locationType}`;
  url += `&checkinDate=${query.get('checkinDate')}`;
  url += `&checkoutDate=${query.get('checkoutDate')}`;
  url += `&boardType=${query.get('boardType')}`;
  let roomsQuery = query.getAll('rooms') || [];
  roomsQuery.forEach(room => {
    url += `&rooms=${room}`;
  });
  let urlParams = new URLSearchParams(window.location.search);
  PASSING_PAGE_PARAMS.forEach(p => {
    if (urlParams.get(p)) url += `&${p}=${urlParams.get(p)}`;
  });
  return url;
};

export interface HotelSearchQueryProps {
  locationCode: string;
  locationType: number;
  checkinDate: string;
  checkoutDate: string;
  rooms: string[];
}
export const getHotelSearchRequest = (search: string, partialRequest?: boolean) => {
  try {
    const query = new URLSearchParams(search);
    let locationCode = query.get('locationCode');
    let locationType = query.get('locationType') as any;
    let checkinDate = query.get('checkinDate') as any;
    let checkoutDate = query.get('checkoutDate') as any;
    let boardType = query.get('boardType') as any;
    if (isNaN(boardType)) {
      boardType = BoardType.NoMeals;
    } else {
      boardType = parseInt(boardType);
    }
    let roomsQuery = query.getAll('rooms') as any;
    let rooms: RoomSearchInfo[] = roomsQuery.map((room, i) => {
      let r = room.split('-');
      return {
        roomIndex: i,
        noOfAdult: parseInt(r[0]),
        noOfChild: parseInt(r[1]),
        childAges:
          r.length >= 3
            ? r[2]
                ?.split('.')
                .filter(x => x)
                .map(x => parseInt(x)) || []
            : [],
      };
    });
    if (partialRequest) {
      let params: CustomHotelSearchRequest = {
        locationCode: query.get('locationCode') as any,
        locationType: parseInt(query.get('locationType') as any) as any,
        checkinDate: query.get('checkinDate') as any,
        checkoutDate: query.get('checkoutDate') as any,
        rooms: rooms,
        boardType: boardType,
        sessionId: query.get('sessionId') as any,
        provider: query.get('provider'),
      };
      return params;
    } else {
      if (locationCode && locationType && checkinDate && checkoutDate && rooms?.length) {
        let params: CustomHotelSearchRequest = {
          locationCode: query.get('locationCode') as any,
          locationType: parseInt(query.get('locationType') as any) as any,
          checkinDate: query.get('checkinDate') as any,
          checkoutDate: query.get('checkoutDate') as any,
          rooms: rooms,
          boardType: boardType,
          sessionId: query.get('sessionId') as any,
          provider: query.get('provider'),
        };
        return params;
      }
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const getHotelDetailRequest = (search: string) => {
  try {
    const query = new URLSearchParams(search);
    let checkinDate = query.get('checkinDate') as any;
    let checkoutDate = query.get('checkoutDate') as any;
    let roomsQuery = query.getAll('rooms') as any;
    let rooms: RoomSearchInfo[] = roomsQuery.map((room, i) => {
      let r = room.split('-');
      return {
        roomIndex: i,
        noOfAdult: parseInt(r[0]),
        noOfChild: parseInt(r[1]),
        childAges:
          r.length >= 3
            ? r[2]
                ?.split('.')
                .filter(x => x)
                .map(x => parseInt(x)) || []
            : [],
      };
    });

    if (checkinDate && checkoutDate && rooms?.length) {
      let params = {
        checkinDate: checkinDate,
        checkoutDate: checkoutDate,
        rooms: rooms,
        sessionId: query.get('sessionId') as any,
        boardType: parseInt(query.get('boardType') as any),
      };
      return params;
    }
    return null;
  } catch (error) {
    return null;
  }
};
